/**
 * v0.12.0 — modo "app" (Capacitor). Dentro do app o endereço do servidor é configurável (rede local nos testes) e o
 * refresh token fica no armazenamento seguro do aparelho, porque a WebView não compartilha cookies com o servidor.
 * Na web nada disso existe: continua o cookie httpOnly. O servidor só entrega o refresh token no corpo quando o pedido
 * traz a Origin do app (capacitor://localhost ou https://localhost), então uma página web não consegue obtê-lo.
 */

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: Record<string, any>;
}

function cap(): CapacitorGlobal | undefined {
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

export const isApp: boolean = (() => {
  try {
    return !!cap()?.isNativePlatform?.();
  } catch {
    return false;
  }
})();

export const platform: "android" | "ios" | "web" = isApp ? ((cap()?.getPlatform?.() as "android" | "ios") ?? "web") : "web";

const SERVER_KEY = "eapace.serverUrl";
const REFRESH_KEY = "eapace.refreshToken";

/** Aceita "192.168.0.10", "meu-pc.local:8443" ou URL completa; devolve "https://host[:porta]" ou null se inválido. */
export function normalizeServerUrl(raw: string): string | null {
  let v = raw.trim();
  if (!v) return null;
  if (!/^https?:\/\//i.test(v)) v = `https://${v}`;
  try {
    const u = new URL(v);
    if (!u.hostname) return null;
    return `${u.protocol}//${u.host}`;
  } catch {
    return null;
  }
}

/** Servidor de fábrica do app de produção (VITE_DEFAULT_SERVER no build do APK); vazio no app de teste e na web. */
const DEFAULT_SERVER = normalizeServerUrl(String(import.meta.env?.VITE_DEFAULT_SERVER ?? ""));

export function getServerUrl(): string | null {
  if (!isApp) return null;
  try {
    return localStorage.getItem(SERVER_KEY) ?? DEFAULT_SERVER;
  } catch {
    return DEFAULT_SERVER;
  }
}

export function setServerUrl(url: string | null): void {
  try {
    if (url) localStorage.setItem(SERVER_KEY, url);
    else localStorage.removeItem(SERVER_KEY);
  } catch {
    /* sem armazenamento: vale só até fechar o app */
  }
}

/** Prefixo dos endereços da API: vazio na web (mesma origem); no app, o servidor escolhido. */
export function apiOrigin(): string {
  return isApp ? (getServerUrl() ?? "") : "";
}

// ---- plugins nativos: pedidos pelo nome ao Capacitor (sem depender dos pacotes npm no build web) ----

const cache: Record<string, any> = {};
/** Plugin nativo pelo nome (null na web ou se o app não o tiver). */
export function nativePlugin(name: string): any | null {
  return hasNativePlugin(name) ? plugin(name) : null;
}
/** O app instalado tem este plugin nativo? (registerPlugin sempre devolve um objeto; isto confere de verdade) */
export function hasNativePlugin(name: string): boolean {
  if (!isApp) return false;
  try {
    return (cap() as any)?.isPluginAvailable?.(name) === true;
  } catch {
    return false;
  }
}
function plugin(name: string): any | null {
  if (!isApp) return null;
  try {
    if (!(name in cache)) cache[name] = (cap() as any)?.registerPlugin?.(name) ?? cap()?.Plugins?.[name] ?? null;
    return cache[name];
  } catch {
    return null;
  }
}

// ---- refresh token (somente app): preferências privadas do app (@capacitor/preferences), senão localStorage ----

export async function loadRefreshToken(): Promise<string | null> {
  if (!isApp) return null;
  try {
    const prefs = plugin("Preferences");
    if (prefs) return ((await prefs.get({ key: REFRESH_KEY })) as { value?: string | null })?.value ?? null;
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export async function saveRefreshToken(token: string | null): Promise<void> {
  if (!isApp) return;
  try {
    const prefs = plugin("Preferences");
    if (prefs) {
      if (token) await prefs.set({ key: REFRESH_KEY, value: token });
      else await prefs.remove({ key: REFRESH_KEY });
      return;
    }
    if (token) localStorage.setItem(REFRESH_KEY, token);
    else localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* sem armazenamento: a sessão vale só até fechar o app */
  }
}

/** Botão voltar do Android: volta na navegação; na primeira tela, sai do app. */
export function setupNativeShell(): void {
  if (!isApp) return;
  document.documentElement.classList.add("is-app", `is-${platform}`);
  const appPlugin = plugin("App");
  appPlugin?.addListener?.("backButton", (ev: { canGoBack?: boolean }) => {
    if (window.history.length > 1 && ev?.canGoBack !== false) window.history.back();
    else appPlugin.exitApp?.();
  });
}

/**
 * v0.19.0 — teclado virtual do celular: ao tocar num campo, a tela marca `kb-open` (o CSS esconde a faixa de arte do
 * login e abre espaço embaixo) e rola o campo para o meio da área visível, para o teclado não cobrir o que se digita.
 * Funciona na web e no app: no app (tela inteira) o sistema nem sempre avisa que o teclado abriu, então não dependemos
 * só da medida do `visualViewport`; quando ela existe, vira `--kb` (altura do teclado).
 */
export function setupKeyboardAssist(): void {
  if (typeof window === "undefined" || typeof matchMedia === "undefined" || !matchMedia("(pointer: coarse)").matches) return;
  const root = document.documentElement;
  const isField = (el: Element | null): el is HTMLElement => {
    if (!el) return false;
    if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
    return el instanceof HTMLInputElement && !["checkbox", "radio", "button", "submit", "range", "file", "color"].includes(el.type);
  };
  const reveal = () => {
    const el = document.activeElement;
    if (isField(el)) el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
  };
  const measure = () => {
    const vv = window.visualViewport;
    const kb = vv ? Math.round(window.innerHeight - vv.height - vv.offsetTop) : 0;
    if (kb > 80) root.style.setProperty("--kb", `${kb}px`);
    else root.style.removeProperty("--kb");
  };
  document.addEventListener("focusin", (e) => {
    if (!isField(e.target as Element)) return;
    root.classList.add("kb-open");
    measure();
    window.setTimeout(reveal, 250);   // espera o teclado terminar de subir
    window.setTimeout(reveal, 600);
  });
  document.addEventListener("focusout", () => {
    window.setTimeout(() => {
      if (isField(document.activeElement)) return;
      root.classList.remove("kb-open");
      root.style.removeProperty("--kb");
    }, 150);
  });
  window.visualViewport?.addEventListener("resize", () => {
    if (!root.classList.contains("kb-open")) return;
    measure();
    reveal();
  });
}

/** Entrega um arquivo baixado: no app, grava no cache e abre a folha de compartilhar/salvar do sistema. */
export async function shareFile(blob: Blob, filename: string): Promise<boolean> {
  const fs = plugin("Filesystem");
  const share = plugin("Share");
  if (!fs || !share) return false;
  const base64: string = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
  const safe = filename.replace(/[^\w.\- ]+/g, "_");
  const saved = await fs.writeFile({ path: safe, data: base64, directory: "CACHE" });
  await share.share({ title: safe, url: saved.uri, dialogTitle: safe });
  return true;
}
